import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  ChartBar, Users, Briefcase, Kanban, Target,
  UsersThree, SignOut, Sparkle, Receipt, GearSix,
  Newspaper, UploadSimple, List, X, Archive, Compass, Star,
  MagnifyingGlass, AddressBook, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import YadidAI from "@/components/YadidAI";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import PWAInstallButton from "@/components/PWAInstallButton";
import GlobalSearch from "@/components/GlobalSearch";

/*
 * Iter 86.x · Hybrid sidebar.
 *
 * Structure: a pinned pair (Command Center, Advisory Desk) at the top with no
 * header label, followed by three semantic groups (CRM / Delivery / Reference).
 * Groups are NOT individually collapsible — the whole sidebar toggles between
 * a full 240px pane and a 60px icon rail via the chevron in the header. In
 * rail mode, labels appear as tooltips on hover (rendered at body level with
 * position: fixed so they escape any scroll-clip). Every route, icon, and
 * data-testid is preserved so muscle memory holds.
 */
const NAV_GROUPS = [
  {
    label: null, // Pinned — no header label, just a divider beneath
    pinned: true,
    items: [
      { to: "/", label: "Command Center", icon: ChartBar, testid: "nav-dashboard" },
      { to: "/notes", label: "Advisory Desk", icon: Sparkle, testid: "nav-notes" },
    ],
  },
  {
    label: "CRM",
    items: [
      { to: "/engagements", label: "Engagements", icon: Briefcase, testid: "nav-engagements" },
      { to: "/clients", label: "Partners", icon: Users, testid: "nav-clients" },
      { to: "/contacts", label: "Contacts", icon: AddressBook, testid: "nav-contacts" },
      { to: "/pipeline", label: "Pipeline", icon: Target, testid: "nav-pipeline" },
    ],
  },
  {
    label: "Delivery",
    items: [
      { to: "/tasks", label: "Tasks", icon: Kanban, testid: "nav-tasks" },
      { to: "/method", label: "Method", icon: Compass, testid: "nav-method" },
      { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
    ],
  },
  {
    label: "Reference",
    items: [
      { to: "/brief", label: "Monday brief", icon: Newspaper, testid: "nav-brief" },
      { to: "/team", label: "Team", icon: UsersThree, testid: "nav-team" },
      { to: "/imports", label: "Imports", icon: UploadSimple, testid: "nav-imports" },
      { to: "/library", label: "Firm library", icon: Archive, testid: "nav-library" },
      { to: "/voice-library", label: "Voice library", icon: Star, testid: "nav-voice-library" },
      { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
    ],
  },
];

const COLLAPSE_KEY = "yadidos.sidebar.collapsed";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Persisted collapse state — read once on mount so the app doesn't flash open.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // Floating tooltip state — only active when the desktop sidebar is collapsed.
  const [tip, setTip] = useState(null); // { text, top, left } | null

  // Auto-close the mobile drawer on route change so tapping a nav item feels native.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Cmd/Ctrl+K opens the global quick-jump palette; Cmd/Ctrl+\ toggles the rail.
  useEffect(() => {
    const onKey = (ev) => {
      const meta = ev.metaKey || ev.ctrlKey;
      if (meta && ev.key.toLowerCase() === "k") {
        ev.preventDefault();
        setSearchOpen((v) => !v);
      } else if (meta && ev.key === "\\") {
        ev.preventDefault();
        setCollapsed((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const showTip = useCallback((el, text) => {
    if (!collapsed) return;
    const r = el.getBoundingClientRect();
    setTip({ text, top: r.top + r.height / 2, left: r.right + 10 });
  }, [collapsed]);
  const hideTip = useCallback(() => setTip(null), []);

  // Whenever the sidebar toggles or the route changes, clear any stale tooltip.
  useEffect(() => { setTip(null); }, [collapsed, location.pathname]);

  const sidebarInner = (isMobile) => {
    // Mobile drawer never collapses — force expanded rendering.
    const isRail = !isMobile && collapsed;
    return (
      <>
        <div className={`border-b sidebar-divider ${isRail ? "py-3 flex justify-center" : "h-16 px-5 flex items-center gap-2.5 justify-between"}`}>
          {isRail && !isMobile ? (
            // Rail mode: the emblem itself is the expand affordance. On hover
            // the logo crossfades to a right-pointing arrow so the control
            // stays discoverable without permanent chrome (MS Copilot pattern).
            <button
              onClick={() => setCollapsed(false)}
              className="sidebar-emblem-toggle group relative w-7 h-7 shrink-0"
              data-testid="sidebar-collapse-toggle"
              aria-label="Expand sidebar"
              title="Expand sidebar (⌘\)"
              onMouseEnter={(e) => showTip(e.currentTarget, "Expand sidebar · ⌘\\")}
              onMouseLeave={hideTip}
            >
              <img
                src="/brand/emblem-ivory.png"
                alt=""
                aria-hidden="true"
                draggable={false}
                className="sidebar-emblem-logo absolute inset-0 w-7 h-7 object-contain select-none pointer-events-none"
              />
              <span className="sidebar-emblem-arrow absolute inset-0 flex items-center justify-center pointer-events-none">
                <CaretRight size={16} weight="bold" />
              </span>
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2.5">
                <img src="/brand/emblem-ivory.png" alt="" aria-hidden="true" draggable={false} className="w-7 h-7 shrink-0 object-contain select-none pointer-events-none" />
                <div>
                  <div className="font-display text-sm font-bold tracking-tight">YadidOS</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest sidebar-muted">Strategy Ops</div>
                </div>
              </div>
              {isMobile ? (
                <button
                  onClick={() => setMobileOpen(false)}
                  className="md:hidden sidebar-muted hover:text-foreground p-1"
                  data-testid="mobile-nav-close"
                  aria-label="Close navigation"
                >
                  <X size={18} />
                </button>
              ) : (
                <button
                  onClick={() => setCollapsed(true)}
                  className="sidebar-toggle-btn"
                  data-testid="sidebar-collapse-toggle"
                  aria-label="Collapse sidebar"
                  title="Collapse sidebar (⌘\)"
                >
                  <CaretLeft size={14} weight="bold" />
                </button>
              )}
            </>
          )}
        </div>

        <nav className="flex-1 py-4 flex flex-col overflow-y-auto">
          {NAV_GROUPS.map((group, gi) => (
            <div
              key={group.label ?? `pinned-${gi}`}
              className={`${group.pinned ? "pb-3 mb-2 border-b sidebar-divider mx-3" : ""}`}
              data-testid={group.label ? `nav-group-${group.label.toLowerCase()}` : "nav-group-pinned"}
            >
              {group.label && !isRail && (
                <div className="px-5 pt-3 pb-2 text-[0.625rem] font-mono uppercase tracking-widest sidebar-muted">
                  {group.label}
                </div>
              )}
              {group.label && isRail && gi > 0 && (
                <div className="h-px bg-[hsl(var(--sidebar-fg)/0.08)] my-2 mx-3" aria-hidden="true" />
              )}
              <div className="flex flex-col gap-0.5">
                {group.items.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    data-testid={testid}
                    onMouseEnter={(e) => showTip(e.currentTarget, label)}
                    onMouseLeave={hideTip}
                    className={({ isActive }) =>
                      `nav-link flex items-center gap-3 text-sm relative ${
                        isRail
                          ? "justify-center py-2.5"
                          : "px-5 py-2.5 border-l-2 border-transparent"
                      } ${isActive ? "active" : ""}`
                    }
                  >
                    <Icon size={16} weight="bold" />
                    {!isRail && <span>{label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className={`p-3 border-t sidebar-divider space-y-2 ${isRail ? "px-2" : "p-4"}`}>
          <button
            data-testid="open-global-search"
            onClick={() => setSearchOpen(true)}
            onMouseEnter={(e) => showTip(e.currentTarget, "Quick search · ⌘K")}
            onMouseLeave={hideTip}
            className={`w-full flex items-center border border-border sidebar-muted hover:border-primary/60 hover:text-primary transition-colors text-sm font-medium ${
              isRail ? "justify-center py-2.5" : "gap-2 px-3 py-2.5"
            }`}
            aria-label="Quick search"
          >
            <MagnifyingGlass size={14} weight="bold" />
            {!isRail && (
              <>
                <span className="flex-1 text-left ml-2">Quick search</span>
                <kbd className="text-[0.625rem] font-mono uppercase tracking-widest border border-border px-1 py-0.5">⌘K</kbd>
              </>
            )}
          </button>
          <button
            data-testid="open-yadid-ai"
            onClick={() => setAiOpen(true)}
            onMouseEnter={(e) => showTip(e.currentTarget, "Ask YadidAi")}
            onMouseLeave={hideTip}
            className={`w-full flex items-center border border-brass/60 text-brass hover:bg-brass-soft transition-colors text-sm font-medium ${
              isRail ? "justify-center py-2.5" : "gap-2 px-3 py-2.5"
            }`}
            aria-label="Ask YadidAi"
          >
            <Sparkle size={16} weight="fill" />
            {!isRail && <span className="ml-2">Ask YadidAi</span>}
          </button>
        </div>

        <div className={`border-t sidebar-divider ${isRail ? "p-2" : "p-4"}`}>
          <div className={`flex items-center ${isRail ? "justify-center" : "gap-3"}`}>
            <div
              className="w-8 h-8 border border-brass/40 bg-brass-soft flex items-center justify-center font-mono text-xs text-brass shrink-0"
              onMouseEnter={(e) => showTip(e.currentTarget, `${user?.name ?? "Signed in"} · ${user?.role ?? ""}`)}
              onMouseLeave={hideTip}
            >
              {user?.name?.[0]?.toUpperCase()}
            </div>
            {!isRail && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate" data-testid="current-user-name">{user?.name}</div>
                  <div className="text-[0.625rem] font-mono uppercase truncate sidebar-muted">{user?.role}</div>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate("/login"); }}
                  className="sidebar-muted hover:text-destructive transition-colors"
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <SignOut size={16} />
                </button>
              </>
            )}
          </div>
          {isRail && (
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              onMouseEnter={(e) => showTip(e.currentTarget, "Sign out")}
              onMouseLeave={hideTip}
              className="w-full flex justify-center py-2 mt-2 sidebar-muted hover:text-destructive transition-colors"
              title="Sign out"
              aria-label="Sign out"
            >
              <SignOut size={14} />
            </button>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="h-screen flex bg-background text-foreground overflow-hidden">
      {/* Desktop sidebar — width animates between 240 (full) and 60 (rail). */}
      <aside
        className={`hidden md:flex shrink-0 border-r border-border yadid-sidebar flex-col h-screen transition-[width] duration-200 ease-out ${collapsed ? "w-[60px]" : "w-60"}`}
        data-testid="desktop-sidebar"
        data-collapsed={collapsed ? "1" : "0"}
      >
        {sidebarInner(false)}
      </aside>

      {/* Mobile drawer + overlay — always renders full-width; no rail mode on mobile. */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          data-testid="mobile-nav-overlay"
        />
      )}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-64 border-r border-border yadid-sidebar flex flex-col transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-nav-drawer"
      >
        {sidebarInner(true)}
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-screen">
        {/* Mobile top bar */}
        <div className="md:hidden h-14 border-b border-border yadid-sidebar flex items-center gap-3 px-4 shrink-0 z-30">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-foreground p-1 -ml-1"
            data-testid="mobile-nav-toggle"
            aria-label="Open navigation"
          >
            <List size={22} weight="bold" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/brand/emblem-navy.png"  alt="" aria-hidden="true" draggable={false} className="w-6 h-6 shrink-0 object-contain select-none pointer-events-none block dark-hide" />
            <img src="/brand/emblem-ivory.png" alt="" aria-hidden="true" draggable={false} className="w-6 h-6 shrink-0 object-contain select-none pointer-events-none dark-show" />
            <div className="font-display text-sm font-bold tracking-tight">YadidOS</div>
          </div>
          <button
            onClick={() => setAiOpen(true)}
            className="ml-auto text-primary p-1"
            aria-label="Ask YadidAi"
            data-testid="mobile-open-yadid-ai"
          >
            <Sparkle size={18} weight="fill" />
          </button>
        </div>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Floating tooltip — only rendered when the desktop sidebar is collapsed
          and a nav element is hovered. Positioned at body level so it never
          gets clipped by the sidebar's internal scroll container. */}
      {tip && (
        <div
          role="tooltip"
          data-testid="sidebar-tooltip"
          className="sidebar-tip"
          style={{ top: tip.top, left: tip.left }}
        >
          {tip.text}
        </div>
      )}

      <YadidAI open={aiOpen} onClose={() => setAiOpen(false)} />
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      <PWAInstallBanner />
    </div>
  );
}
